// Vite ビルド時 env と /config/runtime.json から取得するランタイム config を統合する。
// ローカル開発時は VITE_AUTH_MODE=mock 既定。デプロイ後はランタイム config が優先される。

import { z } from "zod";

const RuntimeConfigSchema = z.object({
  apiBaseUrl: z.string().min(1).default("/api"),
  authMode: z.enum(["mock", "required"]).default("mock"),
  authServiceOrigin: z.string().url().optional(),
  serviceOrigin: z.string().url().optional(),
  realtimeWebSocketUrl: z.string().min(1).optional(),
  invitationSignupUrl: z.string().url().optional(),
});

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

export interface WebConfig {
  readonly authMode: "mock" | "required";
  readonly apiBaseUrl: string;
  readonly authServiceOrigin?: string | undefined;
  readonly serviceOrigin?: string | undefined;
  readonly realtimeWebSocketUrl: string;
  readonly invitationSignupUrl?: string | undefined;
}

let cachedConfig: WebConfig | undefined;

/**
 * 起動時に呼び出して runtime config を読み込む。
 * 失敗した場合はビルド時 env で代替する（ローカル mock 動作の保険）。
 */
export async function initializeWebConfig(): Promise<WebConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }
  const buildTime = readBuildTimeEnv();
  let runtime: RuntimeConfig | undefined;
  try {
    const response = await fetch("/config/runtime.json", { cache: "no-cache" });
    if (response.ok) {
      runtime = RuntimeConfigSchema.parse(await response.json());
    }
  } catch {
    // ローカル開発などで runtime.json が存在しない場合はビルド時 env のみで動作する。
  }

  cachedConfig = {
    authMode: runtime?.authMode ?? buildTime.authMode,
    apiBaseUrl: runtime?.apiBaseUrl ?? buildTime.apiBaseUrl,
    authServiceOrigin: runtime?.authServiceOrigin ?? buildTime.authServiceOrigin,
    serviceOrigin: runtime?.serviceOrigin ?? buildTime.serviceOrigin,
    realtimeWebSocketUrl: runtime?.realtimeWebSocketUrl ?? buildTime.realtimeWebSocketUrl,
    invitationSignupUrl: runtime?.invitationSignupUrl ?? buildTime.invitationSignupUrl,
  };
  return cachedConfig;
}

export function getWebConfig(): WebConfig {
  if (!cachedConfig) {
    throw new Error("WebConfig が未初期化です。initializeWebConfig() を呼び出してください。");
  }
  return cachedConfig;
}

function readBuildTimeEnv(): WebConfig {
  const env = import.meta.env;
  const authMode = env.VITE_AUTH_MODE === "required" ? "required" : "mock";
  return {
    authMode,
    apiBaseUrl: env.VITE_API_BASE_URL ?? "/api",
    authServiceOrigin: env.VITE_MEIDEN_CONNECT_AUTH_SERVICE_ORIGIN,
    serviceOrigin: env.VITE_MEIDEN_CONNECT_SERVICE_ORIGIN,
    realtimeWebSocketUrl: env.VITE_REALTIME_WEBSOCKET_URL ?? "ws://127.0.0.1:8787/realtime",
    invitationSignupUrl: env.VITE_MEIDEN_CONNECT_INVITATION_SIGNUP_URL,
  };
}
